import { yupResolver } from '@hookform/resolvers/yup';
import { useCreateOtherExpense } from 'api-hooks/other-expense';
import { SaleModel } from 'api-hooks/sale/common';
import { TourModel } from 'api-hooks/tour/tour-list/tour-detail';
import formSetErrors from 'common/helpers/form-setError';
import DrawerHeader from 'components/common/drawer/drawer-header';
import { DrawerBody } from 'components/common/drawer/styles';
import notification from 'components/common/notification';
import Separator from 'components/common/separator';
import { Form, Input } from 'components/elements';
import Button from 'components/elements/buttons/button';
import useDismiss from 'hooks/use-dismiss';
import { FullContainer } from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';
import { useForm } from 'react-hook-form';

import {
  OtherExpenseCreateFormSchema,
  OtherExpenseCreateFormType,
  OtherExpenseDetailCreateFormType,
} from './components/form-type';
import OtherExpenseCard from './components/other-expense-card';

interface OtherExpenseCreateProps {
  expensableRefType: 'Tour' | 'Sale';
  data: TourModel | SaleModel;
  onClose: () => void;
}

export default function OtherExpenseCreate(props: OtherExpenseCreateProps) {
  const { expensableRefType, data } = props;
  const onDismiss = useDismiss('other-expenses');
  const { mutateAsync } = useCreateOtherExpense();
  const { t } = useTranslation();

  const defaultValues = React.useMemo<OtherExpenseCreateFormType>(() => {
    return {
      expensableRefId: data.refId,
      expensableRefType,
      otherExpenses: [
        {
          accountableId: null,
          accountType: null,
          amount: undefined,
          description: '',
          transactionAt: new Date(),
        } as unknown as OtherExpenseDetailCreateFormType,
      ],
    };
  }, [data.refId, expensableRefType]);

  const methods = useForm({
    defaultValues,
    resolver: yupResolver(OtherExpenseCreateFormSchema()),
  });

  const onSubmit = React.useCallback(
    async (values: OtherExpenseCreateFormType) => {
      try {
        const result = await mutateAsync(values);
        notification.success({
          message: result.message,
        });
        onDismiss();
        props.onClose();
      } catch (e) {
        if (e.errors) {
          formSetErrors(e.errors, methods.setError);
        }
        e.message &&
          notification.error({
            message: e.message,
          });
      }
    },
    [methods.setError, mutateAsync, onDismiss, props],
  );

  return (
    <>
      <DrawerHeader
        onClose={props.onClose}
        title={t('common:add_extra', { extra: t('common:other_expenses') })}
      />
      <DrawerBody
        style={{
          marginTop: 0,
        }}
      >
        <Form
          methods={methods}
          onSubmit={onSubmit}
          keyName={`create-otherExpense-${expensableRefType}-${data.refId}`}
          noReset
        >
          <OtherExpenseCard type="create" />
          <Separator gap={16} />

          <FullContainer
            style={{ display: 'flex', justifyContent: 'end', gap: 8 }}
          >
            <Button buttonType="tertiary" onClick={props.onClose}>
              {t('common:cancel')}
            </Button>
            <Input type="submit" />
          </FullContainer>
        </Form>
      </DrawerBody>
    </>
  );
}
